import { useCallback, useEffect, useState } from 'react';
import runsService from '../../services/runs';
import useInterval from '../Util/useInterval';

export type RunTask = {
  id?: number;
  name?: string;
  status?: string;
  state?: string;
  stateMessage?: string;
  cluster?: string;
  startTime?: number;
  endTime?: number;
  percent?: number | null;
};

export type RunProgress = {
  totalTasks?: number;
  succeededTasks?: number;
  failedTasks?: number;
  percent?: number;
};

export type RunMetadata = {
  pipelineRunId?: string;
  jobId?: number;
  jobRunId?: number;
  clusterName?: string;
  runPageUrl?: string;
  status?: string;
  state?: string;
  stateMessage?: string;
  startTime?: number;
  endTime?: number;
  tasks?: RunTask[];
  progress?: RunProgress;
};

const toMillis = (value?: string | number | null) => {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const POLL_INTERVAL_MS = 15000;

const FINISHED_STATES = ['TERMINATED', 'INTERNALERROR', 'SKIPPED', 'SUCCEEDED', 'FAILED', 'CANCELLED', 'CANCELED'];

const normalizeState = (state?: string | null) => (state || '').replace(/[^a-z]/gi, '').toUpperCase();

export const isRunFinished = (metadata?: RunMetadata | null) =>
  !!metadata &&
  (FINISHED_STATES.includes(normalizeState(metadata.state)) ||
    (typeof metadata.endTime === 'number' && metadata.endTime > 0));

const parseDates = (raw: any): RunMetadata => ({
  ...raw,
  startTime: toMillis(raw?.startTime),
  endTime: toMillis(raw?.endTime),
  tasks: (Array.isArray(raw?.tasks) ? raw.tasks : []).map((t: any) => ({
    ...t,
    startTime: toMillis(t?.startTime),
    endTime: toMillis(t?.endTime),
  })),
});

const useRunPipelineMetadata = (runId?: string, enabled = true) => {
  const [data, setData] = useState<RunMetadata | null>(null);
  const [error, setError] = useState<boolean>(false);
  const active = Boolean(runId && enabled) && !isRunFinished(data);

  const load = useCallback(async () => {
    if (!runId || !enabled) return;
    try {
      const res = await runsService.getRunMetadata(runId);
      setData(parseDates(res));
      setError(false);
    } catch (err) {
      console.error('Failed to fetch run metadata', err);
      setError(true);
    }
  }, [runId, enabled]);

  useEffect(() => {
    setData(null);
    setError(false);
    load();
  }, [load]);
  useInterval(load, active ? POLL_INTERVAL_MS : null);

  return { data, error };
};

export default useRunPipelineMetadata;
